import type { InspectApprovalActionResult } from '@ankor-io/common/types/approval-workflows/types'
import type { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import type { ReceiptCaptureDraftPayload } from '@ankor-io/common/types/receipt-capture'
import type { EditTransactionInspectionResult, EditableFields } from '@ankor-io/common/types/transaction/edit'
import type { TaxLabelPayload } from '@ankor-io/common/types/transaction/tax'
import type {
  ConfirmTransactionAttachmentUploadRequest,
  CreateOpsManualTransactionBody,
  CreateTransactionAttachmentBody,
  FullTransactionPayload,
  ListTransactionsRequest,
  PrepareTransactionAttachmentUploadRequest,
  PrepareTransactionAttachmentUploadResponse,
  TransactionAttachmentPayload,
  TransactionPayload,
  TransactionStatus,
  UpdateAnkorPayTransactionBody,
  UpdateManualTransactionBody,
} from '@ankor-io/common/types/transaction/transaction'

import { type HttpConfig, http } from '@/api/client/http'
import { createVesselConnectedAccountPaths } from '@/api/paths'

/** Longer timeout for receipt OCR; default axios client uses 20s which is often too short. */
const RECEIPT_CAPTURE_TIMEOUT_MS = 120000

/**
 * Build query parameters for transaction list requests.
 * Shared helper to ensure consistency between listTransactions and listMyTransactions.
 */
function buildTransactionQueryParams(params?: ListTransactionsRequest): URLSearchParams {
  const searchParams = new URLSearchParams()

  if (params?.status && params.status.length > 0) {
    searchParams.set('status', params.status.join(','))
  }

  if (params?.type && params.type.length > 0) {
    searchParams.set('type', params.type.join(','))
  }

  if (params?.charterId) {
    searchParams.set('charterId', params.charterId)
  }

  if (params?.categoryId) {
    searchParams.set('categoryId', params.categoryId)
  }

  if (params?.vendor) {
    searchParams.set('vendor', params.vendor)
  }

  if (params?.dateFrom) {
    searchParams.set('dateFrom', params.dateFrom)
  }

  if (params?.dateTo) {
    searchParams.set('dateTo', params.dateTo)
  }

  if (params?.amountMinCents !== undefined) {
    searchParams.set('amountMinCents', String(params.amountMinCents))
  }

  if (params?.amountMaxCents !== undefined) {
    searchParams.set('amountMaxCents', String(params.amountMaxCents))
  }

  if (params?.take !== undefined) {
    searchParams.set('take', String(params.take))
  }

  if (params?.skip !== undefined) {
    searchParams.set('skip', String(params.skip))
  }

  if (params?.sortBy) {
    searchParams.set('sortBy', params.sortBy)
  }

  if (params?.sortOrder) {
    searchParams.set('sortOrder', params.sortOrder)
  }

  if (params?.charterTagged !== undefined) {
    searchParams.set('charterTagged', String(params.charterTagged))
  }

  if (params?.createdBy) {
    searchParams.set('createdBy', params.createdBy)
  }

  if (params?.paymentMethod && params.paymentMethod.length > 0) {
    searchParams.set('paymentMethod', params.paymentMethod.join(','))
  }

  if (params?.excludeFailed !== undefined) {
    searchParams.set('excludeFailed', String(params.excludeFailed))
  }

  return searchParams
}

export const transactionService = {
  // ========== GET Methods ==========

  /**
   * List transactions for an organization and connected account.
   */
  listTransactions(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    params?: ListTransactionsRequest,
    config?: HttpConfig,
  ): Promise<TransactionPayload[]> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const transactionsPath = paths.transactions()

    const searchParams = buildTransactionQueryParams(params)
    const queryString = searchParams.toString()
    const url = queryString ? `${transactionsPath}?${queryString}` : transactionsPath

    return http.get<TransactionPayload[]>(url, config)
  },

  /**
   * List my transactions for an organization and connected account.
   */
  listMyTransactions(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    params?: ListTransactionsRequest,
    config?: HttpConfig,
  ): Promise<TransactionPayload[]> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const myTransactionsPath = paths.myTransactions()

    const searchParams = buildTransactionQueryParams(params)
    const queryString = searchParams.toString()
    const url = queryString ? `${myTransactionsPath}?${queryString}` : myTransactionsPath

    return http.get<TransactionPayload[]>(url, config)
  },

  /**
   * Get a transaction by ID for an organization and connected account.
   * Returns FullTransactionPayload which includes charter details and AWX activity event.
   */
  getTransactionById(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    config?: HttpConfig,
  ): Promise<FullTransactionPayload> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.transactionById(transactionId)

    return http.get<FullTransactionPayload>(url, config)
  },

  /**
   * Inspect a transaction for editing for an organization and connected account.
   * Returns an Edit transaction Inspection Result object that describes which fields are editable and why.
   */
  inspectTransactionEdit(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    config?: HttpConfig,
  ): Promise<EditTransactionInspectionResult> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.transactionEditInspect(transactionId)

    return http.get<EditTransactionInspectionResult>(url, config)
  },

  /**
   * Inspect whether the current user can approve, reject, or force this transaction.
   * GET /transaction/:transactionId/status/inspect
   */
  inspectApprovalAction(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    config?: HttpConfig,
  ): Promise<InspectApprovalActionResult> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.transactionStatusInspect(transactionId)

    return http.get<InspectApprovalActionResult>(url, config)
  },

  /**
   * List transaction attachments for an organization and connected account.
   */
  listTransactionAttachments(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    params?: { take?: number; skip?: number },
    config?: HttpConfig,
  ): Promise<TransactionAttachmentPayload[]> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const attachmentsPath = paths.transactionAttachments(transactionId)

    const searchParams = new URLSearchParams()
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }

    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${attachmentsPath}?${queryString}` : attachmentsPath

    return http.get<{ attachments: TransactionAttachmentPayload[] }>(url, config).then((res) => res.attachments)
  },

  /**
   * Get a transaction attachment by ID for an organization and connected account.
   */
  getTransactionAttachmentById(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    attachmentId: string,
    config?: HttpConfig,
  ): Promise<TransactionAttachmentPayload> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.transactionAttachmentById(transactionId, attachmentId)

    return http.get<{ attachment: TransactionAttachmentPayload }>(url, config).then((res) => res.attachment)
  },

  // ========== POST Methods ==========

  /**
   * Update transaction status for an organization and connected account.
   * `reason` is optional and is recorded on the expense activity log when provided.
   */
  updateTransactionStatus(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    status: TransactionStatus,
    force?: boolean,
    reason?: string,
    config?: HttpConfig,
  ): Promise<TransactionStatus> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.transactionStatus(transactionId)

    return http.post<{ status: TransactionStatus }>(url, { status, force, reason }, config).then((res) => res.status)
  },

  /**
   * Create a transaction attachment for an organization and connected account.
   */
  createTransactionAttachment(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    payload: CreateTransactionAttachmentBody,
    config?: HttpConfig,
  ): Promise<TransactionAttachmentPayload> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.transactionAttachments(transactionId)

    return http
      .post<{ attachment: TransactionAttachmentPayload }, CreateTransactionAttachmentBody>(url, payload, config)
      .then((res) => res.attachment)
  },

  /**
   * Prepare transaction attachment upload for an organization and connected account.
   */
  prepareTransactionAttachmentUpload(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    payload: PrepareTransactionAttachmentUploadRequest,
    config?: HttpConfig,
  ): Promise<PrepareTransactionAttachmentUploadResponse> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.transactionAttachmentPrepare(transactionId)

    return http.post<PrepareTransactionAttachmentUploadResponse, PrepareTransactionAttachmentUploadRequest>(
      url,
      payload,
      config,
    )
  },

  /**
   * Confirm transaction attachment upload for an organization and connected account.
   */
  confirmTransactionAttachmentUpload(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    payload: ConfirmTransactionAttachmentUploadRequest,
    config?: HttpConfig,
  ): Promise<TransactionAttachmentPayload> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.transactionAttachmentConfirm(transactionId)

    return http.post<TransactionAttachmentPayload, ConfirmTransactionAttachmentUploadRequest>(url, payload, config)
  },

  /**
   * Tax labels visible on this vessel connected account (OPS manual expense picker).
   */
  listTaxLabelsForConnectedAccount(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    config?: HttpConfig,
  ): Promise<TaxLabelPayload[]> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.taxLabels()

    return http.get<TaxLabelPayload[]>(url, config)
  },

  /**
   * Create an ops (operations) manual transaction for an organization and connected account.
   */
  createOpsTransaction(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    payload: CreateOpsManualTransactionBody,
    config?: HttpConfig,
  ): Promise<TransactionPayload> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.opsTransaction()

    return http.post<TransactionPayload, CreateOpsManualTransactionBody>(url, payload, config)
  },

  /**
   * Extract receipt draft from an image for manual expense prefill (multipart field `image`).
   */
  captureReceiptDraft(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    awxConnectedAccountType: AwxConnectedAccountType,
    imageFile: File,
    config?: HttpConfig,
  ): Promise<ReceiptCaptureDraftPayload> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const path = paths.receiptCapture()
    const params = new URLSearchParams({ awxConnectedAccountType })
    const url = `${path}?${params.toString()}`

    const formData = new FormData()
    formData.append('image', imageFile)

    return http.post<ReceiptCaptureDraftPayload, FormData>(url, formData, {
      ...config,
      timeout: config?.timeout ?? RECEIPT_CAPTURE_TIMEOUT_MS,
    })
  },

  // ========== PUT Methods ==========

  /**
   * Update a transaction for an organization and connected account.
   */
  updateTransaction(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    payload: UpdateManualTransactionBody | UpdateAnkorPayTransactionBody,
    config?: HttpConfig,
  ): Promise<TransactionPayload> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.transactionById(transactionId)

    return http.put<TransactionPayload, UpdateManualTransactionBody | UpdateAnkorPayTransactionBody>(
      url,
      payload,
      config,
    )
  },

  /**
   * Update OPS transaction for an organization and connected account.
   * Uses the module-specific `/ops/transaction/:transactionId` endpoint.
   */
  updateOpsTransaction(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    payload: UpdateManualTransactionBody | UpdateAnkorPayTransactionBody,
    config?: HttpConfig,
  ): Promise<TransactionPayload> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.opsTransactionById(transactionId)

    return http.put<TransactionPayload, UpdateManualTransactionBody | UpdateAnkorPayTransactionBody>(
      url,
      payload,
      config,
    )
  },

  /**
   * Commit an edit to a transaction for an organization and connected account.
   * Uses the new inspect/commit endpoint. The payload is a subset of the
   * editable transaction fields; the backend validates each supplied field.
   *
   * NOTE: the backend can reject the commit with an ApplicationError whose
   * `clientInfo` is returned in the HTTP error body under `info`, should the caller surface
   * this to the user?? (to be figured out)
   */
  commitTransaction(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    payload: EditableFields,
    config?: HttpConfig,
  ): Promise<Omit<FullTransactionPayload, 'transactedBy'>> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.transactionCommit(transactionId)

    return http.put<Omit<FullTransactionPayload, 'transactedBy'>, EditableFields>(url, payload, config)
  },

  // ========== DELETE Methods ==========

  /**
   * Delete a transaction for an organization and connected account.
   */
  deleteTransaction(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    config?: HttpConfig,
  ): Promise<void> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.transactionById(transactionId)

    return http.delete<{ success: boolean }>(url, config).then(() => undefined)
  },

  /**
   * Delete a transaction attachment for an organization and connected account.
   */
  deleteTransactionAttachment(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    transactionId: string,
    attachmentId: string,
    config?: HttpConfig,
  ): Promise<void> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.transactionAttachmentById(transactionId, attachmentId)

    return http.delete<{ success: boolean }>(url, config).then(() => undefined)
  },
}
